import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const ContactForm = ({ propertyId = null, propertyName = null }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: propertyName ? `${t('contactForm.interestedIn')}: ${propertyName}` : "",
    interest_type: "General",
    property_id: propertyId,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('contactForm.requiredFields'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('contactForm.success'));
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        interest_type: "General",
        property_id: propertyId,
      });
    } catch (error) {
      toast.error(t('contactForm.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="contact-form space-y-5" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">{t('contactForm.name')} *</label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('contactForm.namePlaceholder')}
            className="h-14 bg-[#121212]/80 border-[#262626] text-white placeholder:text-white/40 focus:border-[#D4AF37]"
            data-testid="contact-name"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">{t('contactForm.emailLabel')} *</label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="tu@email.com"
            className="h-14 bg-[#121212]/80 border-[#262626] text-white placeholder:text-white/40 focus:border-[#D4AF37]"
            data-testid="contact-email"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">{t('contactForm.phone')}</label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+502 0000-0000"
            className="h-14 bg-[#121212]/80 border-[#262626] text-white placeholder:text-white/40 focus:border-[#D4AF37]"
            data-testid="contact-phone"
          />
        </div>
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">{t('contactForm.interest')}</label>
          <Select
            value={formData.interest_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, interest_type: value }))}
          >
            <SelectTrigger 
              className="h-14 bg-[#121212]/80 border-[#262626] text-white"
              data-testid="contact-interest"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#262626]">
              <SelectItem value="General">{t('contactForm.interestGeneral')}</SelectItem>
              <SelectItem value="Compra">{t('contactForm.interestBuy')}</SelectItem>
              <SelectItem value="Venta">{t('contactForm.interestSell')}</SelectItem>
              <SelectItem value="Alquiler">{t('contactForm.interestRent')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm text-[#A3A3A3] mb-2">{t('contactForm.message')} *</label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder={t('contactForm.messagePlaceholder')}
          rows={5}
          className="bg-[#121212]/80 border-[#262626] text-white placeholder:text-white/40 focus:border-[#D4AF37] resize-none"
          data-testid="contact-message"
          required
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-14 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-medium uppercase tracking-wider"
        data-testid="contact-submit"
      >
        {loading ? (
          <Loader2 className="animate-spin" size={20} />
        ) : (
          <>
            <Send size={18} className="mr-2" />
            {t('contactForm.submit')}
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
